import axios from 'axios';
import { API } from './api';

/**
 * Dashboard Service
 * Handles all API calls and data processing for the dashboard
 */
class DashboardService {
    /**
     * Fetch dashboard statistics from the API
     * @returns {Promise} Promise object with dashboard data
     */
    async getDashboardStats() {
        try {
            
            
            // Get the authentication token
            const token = this.getAuthToken();
            
            // If no token is available, use mock data
            if (!token) {
                console.warn('No authentication token available. Using mock data.');
                return this.getMockData();
            }
            
            // Use the apiClient which already has the token handling logic
           
            const response = await axios.get(`${API}/dashboard/stats`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            
            if (!response.data) {
                throw new Error('API response is missing data');
            }

            // Check if the data has the expected structure
            if (response.data.data) {
                return this.processData(response.data.data);
            } else if (response.data.status !== undefined) {
                return this.processData(response.data);
            } else {
                
                return this.processData(response.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.error('Error status:', error.response.status);
                console.error('Error headers:', error.response.headers);
                console.error('Error data:', error.response.data);
            } else if (error.request) {
                // The request was made but no response was received
                console.error('Error request:', error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.error('Error message:', error.message);
            }
            
            // Use mock data as fallback
            console.warn('API call failed. Using mock data as fallback.');
            return this.getMockData();
        }
    }

    /**
     * Get the authentication token from session storage
     * @returns {string|null} The authentication token or null if not found
     */
    getAuthToken() {
        try {
            const storedAuthState = sessionStorage.getItem('authLocalstorage');
            if (!storedAuthState) {
                console.warn('No authentication data found in session storage');
                return null;
            }

            const parsedAuthState = JSON.parse(storedAuthState);
            if (!parsedAuthState || typeof parsedAuthState !== 'object') {
                console.warn('Invalid authentication data format');
                return null;
            }

            const token = parsedAuthState.token;
            if (!token) {
                console.warn('Token not found in authentication data');
                return null;
            }

            return token;
        } catch (error) {
            console.error('Error retrieving authentication token:', error);
            return null;
        }
    }

    /**
     * Process and transform dashboard data
     * @param {Object} data Raw data from API
     * @returns {Object} Processed data
     */
    processData(data) {
        // Create a default structure for the dashboard data
        const defaultData = {
            summary: {
                totalVoyages: 0,
                activeVoyages: 0,
                completedVoyages: 0,
                totalRevenue: 0,
                monthlyStats: {
                    voyages: 0,
                    revenue: 0
                }
            },
            monthlyRevenue: [],
            voyagesByType: [],
            topRoutes: [],
            vehicleStatus: [],
            fuelConsumption: [],
            clientDistribution: [],
            driverPerformance: [],
            upcomingMaintenance: [],
            revenueByRegion: []
        };

        // If no data is returned, return the default structure
        if (!data) {
            return defaultData;
        }

        // Process summary data
        if (data.summary) {
            defaultData.summary = {
                ...defaultData.summary,
                ...data.summary
            };
        }

        // Process monthly revenue data
        if (data.monthlyRevenue && Array.isArray(data.monthlyRevenue)) {
            defaultData.monthlyRevenue = data.monthlyRevenue.map(item => ({
                date: item.date || '',
                revenue: parseFloat(item.revenue) || 0,
                count: parseInt(item.count) || 0
            }));
        }

        // Process voyages by type data
        if (data.voyagesByType && Array.isArray(data.voyagesByType)) {
            defaultData.voyagesByType = data.voyagesByType.map(item => ({
                name: item.name || 'Unknown',
                value: parseInt(item.value) || 0
            }));
        }

        // Process top routes data
        if (data.topRoutes && Array.isArray(data.topRoutes)) {
            defaultData.topRoutes = data.topRoutes.map(item => ({
                route: item.route || 'Unknown',
                count: parseInt(item.count) || 0
            }));
        }

        // Process vehicle status data
        if (data.vehicleStatus && Array.isArray(data.vehicleStatus)) {
            defaultData.vehicleStatus = data.vehicleStatus.map(item => ({
                status: item.status || 'Unknown',
                value: parseInt(item.value) || 0
            }));
        }

        // Process fuel consumption data
        if (data.fuelConsumption && Array.isArray(data.fuelConsumption)) {
            defaultData.fuelConsumption = data.fuelConsumption.map(item => ({
                month: item.month || 'Unknown',
                total_quantity: parseFloat(item.total_quantity) || 0,
                total_cost: parseFloat(item.total_cost) || 0
            }));
        }

        // Process client distribution data
        if (data.clientDistribution && Array.isArray(data.clientDistribution)) {
            defaultData.clientDistribution = data.clientDistribution.map(item => ({
                Type_Client: item.Type_Client || 'Unknown',
                count: parseInt(item.count) || 0
            }));
        }

        // Process driver performance data
        if (data.driverPerformance && Array.isArray(data.driverPerformance)) {
            defaultData.driverPerformance = data.driverPerformance.map(item => ({
                Nom: item.chauffeur_nom || 'Unknown',
                total_voyages: parseInt(item.total_voyages) || 0,
                avg_revenue: parseFloat(item.avg_revenue) || 0
            }));
        }

        // Process upcoming maintenance data
        if (data.upcomingMaintenance && Array.isArray(data.upcomingMaintenance)) {
            defaultData.upcomingMaintenance = data.upcomingMaintenance.map(item => ({
                matricule: item.matricule || 'N/A',
                DateExpirationAssurance: item.DateExpirationAssurance || item.date_fin_assurance || new Date()
            }));
        }

        // Process revenue by region data
        if (data.revenueByRegion && Array.isArray(data.revenueByRegion)) {
            defaultData.revenueByRegion = data.revenueByRegion.map(item => ({
                region: item.region || 'Unknown',
                total_revenue: parseFloat(item.total_revenue) || 0,
                total_voyages: parseInt(item.total_voyages) || 0
            }));
        }

        return defaultData;
    }

    /**
     * Generate mock data for testing
     * @returns {Object} Mock dashboard data
     */


    /**
     * Get dashboard data with no fallback
     * @returns {Promise} Promise object with dashboard data
     */
    async getDashboardData() {
        
        return await this.getDashboardStats();
    }

    /**
     * Test the API connection with a direct fetch
     * @returns {Promise} Promise object with raw API response
     */
     async testApiConnection() {
        try {
            
            
            // Get the authentication token
            const token = this.getAuthToken();
            
            // If no token is available, return an error status
            if (!token) {
                console.warn('No authentication token available for API test.');
                return {
                    status: 'error',
                    statusText: 'Authentication token not found',
                    message: 'Please log in to test the API connection',
                    data: null
                };
            }
            
            // Make a direct fetch request to test CORS and basic connectivity
            const response = await fetch(`${API}/dashboard/stats`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            
            // Handle the response                      
            const data = await response.json();        
            return {
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries([...response.headers.entries()]),
                data: data
            };
        } catch (error) {
            console.error('Error testing API connection:', error);
            return {
                status: 'error',
                statusText: error.message || 'Unknown error',
                message: 'Failed to connect to the API. See console for details.',
                data: null
            };
        }
    }
}

export default new DashboardService();
